/**
 * Single source of truth for the ToxicDetector pricing system.
 * Every pricing surface (landing section, onboarding, upload page, settings
 * billing) renders from this data so the plans stay one coherent system.
 *
 * Payment processing is intentionally NOT defined here — paid plans expose
 * their checkout identifier only, which is handed to `onCheckout(plan)`.
 * The four checkout identifiers below are the exact values the payment
 * Edge Function accepts.
 */
export type CheckoutPlanId = 'single_scan' | 'weekly' | 'monthly' | 'yearly'
export type PricingPlanId = 'free' | CheckoutPlanId
/**
 * Live list price per plan, in US dollars. Every price string in this file —
 * and any analytics or receipt amount elsewhere — derives from these numbers,
 * so a price change happens in exactly one place.
 */
export const PLAN_PRICES_USD: Record<CheckoutPlanId, number> = {
  single_scan: 2.99,
  weekly: 9.99,
  monthly: 29.99,
  yearly: 149.99,
}
/**
 * Formats a USD amount for display, e.g. 29.99 → "$29.99", 0 → "$0".
 * Whole amounts drop the cents so free/round prices read cleanly.
 */
export function formatUsd(amount: number): string {
  const rounded = Math.round(amount * 100) / 100
  const fractionDigits = Number.isInteger(rounded) ? 0 : 2
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(rounded)
}
/** 12 months of Monthly — the reference price Yearly is compared against. */
export const YEARLY_REFERENCE_USD =
  Math.round(PLAN_PRICES_USD.monthly * 12 * 100) / 100
export const YEARLY_SAVINGS_USD =
  Math.round((YEARLY_REFERENCE_USD - PLAN_PRICES_USD.yearly) * 100) / 100
export const YEARLY_SAVINGS_PERCENT = Math.round(
  (YEARLY_SAVINGS_USD / YEARLY_REFERENCE_USD) * 100,
)
export type PlanAccent = 'neutral' | 'gold' | 'red'
/**
 * Visual weight inside the pricing system.
 * lead      — the free entry point, rendered as a lighter lead-in strip
 * standard  — baseline paid card
 * secondary — second strongest premium card (Yearly)
 * primary   — dominant recommended card (Monthly)
 */
export type PlanEmphasis = 'lead' | 'standard' | 'secondary' | 'primary'
export interface PricingPlan {
  id: PricingPlanId
  /** Marketing name, e.g. "Pay Per Scan". */
  name: string
  /** Uppercase system label used on authenticated status surfaces. */
  label: string
  glyph: string
  price: string
  /** Price with its interval, e.g. "$29.99 / month". */
  priceLabel: string
  /** Billing interval on its own, e.g. "Monthly". */
  billing: string
  /** One-line scan entitlement this plan grants. */
  entitlement: string
  /** Very short line for compact plan cards. */
  summary: string
  /**
   * Undiscounted reference price, shown struck through beside the price.
   * Only set where a real comparison exists (Yearly vs. 12× Monthly).
   */
  originalPrice?: string
  /** Short savings chip, e.g. "Save 58%". */
  savings?: string
  cadence: string
  features: string[]
  cta: string
  badge?: string
  note?: string
  accent: PlanAccent
  emphasis: PlanEmphasis
}
export const freePlan: PricingPlan = {
  id: 'free',
  name: 'Just Curious',
  label: 'FREE',
  glyph: '🆓',
  price: formatUsd(0),
  priceLabel: formatUsd(0),
  billing: 'Forever',
  entitlement: '1 free forensic scan',
  summary: '1 free scan',
  cadence: 'Forever',
  features: [
    '1 free forensic scan',
    'Toxicity score & risk level',
    'Detected pattern names',
    'High-level explanations',
  ],
  cta: 'Start free scan',
  accent: 'neutral',
  emphasis: 'lead',
}
export const paidPlans: PricingPlan[] = [
  {
    id: 'single_scan',
    name: 'Pay Per Scan',
    label: 'SINGLE SCAN',
    glyph: '🔥',
    price: formatUsd(PLAN_PRICES_USD.single_scan),
    priceLabel: `${formatUsd(PLAN_PRICES_USD.single_scan)} one-time`,
    billing: 'One-time',
    entitlement: 'Complete forensic report',
    summary: 'Complete forensic report',
    cadence: 'One-time payment',
    note: 'No subscription required.',
    features: [
      'Complete forensic report',
      'Message-by-message evidence',
      'Detailed pattern analysis',
      'Personalized recommendations',
      'Downloadable PDF report',
      'Shareable forensic result',
    ],
    cta: 'Get the complete report',
    accent: 'neutral',
    emphasis: 'standard',
  },
  {
    id: 'weekly',
    name: 'Weekly',
    label: 'WEEKLY',
    glyph: '⚡',
    price: formatUsd(PLAN_PRICES_USD.weekly),
    priceLabel: `${formatUsd(PLAN_PRICES_USD.weekly)} / week`,
    billing: 'Weekly',
    entitlement: 'Unlimited forensic scans',
    summary: 'Unlimited scans',
    cadence: 'Per week',
    features: [
      'Unlimited forensic scans while active',
      'Advanced analysis modes',
      'Detailed pattern detection',
      'Priority processing',
      'Weekly relationship insights',
      'Shareable results',
      'PDF reports',
    ],
    cta: 'Start weekly',
    accent: 'neutral',
    emphasis: 'standard',
  },
  {
    id: 'monthly',
    name: 'Monthly',
    label: 'MONTHLY',
    glyph: '⭐',
    price: formatUsd(PLAN_PRICES_USD.monthly),
    priceLabel: `${formatUsd(PLAN_PRICES_USD.monthly)} / month`,
    billing: 'Monthly',
    entitlement: 'Unlimited forensic scans',
    summary: 'Unlimited scans',
    cadence: 'Per month',
    badge: 'Most popular',
    features: [
      'Unlimited forensic scans',
      'Full report on every scan',
      'Advanced pattern detection',
      'Historical tracking & relationship trends',
      'Priority processing',
      'Full PDF case files',
      'All advanced analysis modes',
    ],
    cta: 'Get monthly access',
    accent: 'red',
    emphasis: 'primary',
  },
  {
    id: 'yearly',
    name: 'Yearly',
    label: 'YEARLY',
    glyph: '💎',
    price: formatUsd(PLAN_PRICES_USD.yearly),
    priceLabel: `${formatUsd(PLAN_PRICES_USD.yearly)} / year`,
    billing: 'Yearly',
    entitlement: 'Unlimited forensic scans',
    summary: 'Unlimited scans',
    cadence: 'Per year',
    // 12 × Monthly is the honest comparison price for a year of access.
    originalPrice: formatUsd(YEARLY_REFERENCE_USD),
    savings: `Save ${YEARLY_SAVINGS_PERCENT}%`,
    badge: 'Best value',
    note: `Everything in Monthly. Paying ${formatUsd(PLAN_PRICES_USD.monthly)} every month costs ${formatUsd(YEARLY_REFERENCE_USD)} a year — you keep ${formatUsd(YEARLY_SAVINGS_USD)}.`,
    features: [
      'Unlimited forensic scans',
      'Persona evolution tracking',
      'Monthly clarity reports',
      'Gaslight alert system',
      'Priority processing queue',
      'Full PDF case files',
      'All advanced analysis modes',
    ],
    cta: 'Get yearly access',
    accent: 'gold',
    emphasis: 'secondary',
  },
]
export const pricingPlans: PricingPlan[] = [freePlan, ...paidPlans]
/** Entry tiers: try it once, or pay for a single report. */
export const entryPlans: PricingPlan[] = [
  freePlan,
  paidPlans.find((plan) => plan.id === 'single_scan') as PricingPlan,
]
/** Unlimited access tiers, ordered weekly → monthly → yearly. */
export const subscriptionPlans: PricingPlan[] = paidPlans.filter((plan) =>
  ['weekly', 'monthly', 'yearly'].includes(plan.id),
)
export function getPlan(id: PricingPlanId): PricingPlan | undefined {
  return pricingPlans.find((plan) => plan.id === id)
}
/** Uppercase system label for a plan, safe for unknown values. */
export function planLabel(id: PricingPlanId): string {
  return getPlan(id)?.label ?? 'FREE'
}
